import React, { useEffect, useMemo, useRef, useState } from 'react'

import { makeStyles } from '@material-ui/core/styles'
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline'

import {
    Area,
    Folder,
    getFlatInfrastructure,
    getServiceImageDetails,
    TemplateService,
    Unit,
} from '../../../../models'

const EZ_TURN_GREEN = '#008c85'

const selectionKey = (templateServiceId: number, areaId: number) =>
    `${templateServiceId}-${areaId}`

const isPaintCategory = (category?: string | null) =>
    (category ?? '').toUpperCase() === 'PAINT'

const areaLabel = (area: Area) => {
    const name = `${area.area_config_name ?? ''} ${
        area.area_label ?? ''
    }`.trim()
    return name || 'Area'
}

const firstSchedulerUnit = (infrastructure: Folder) => {
    const first = getFlatInfrastructure(infrastructure).find(
        (item): item is Unit => 'areas' in item && !('children' in item),
    )
    return first ?? null
}

interface Props {
    infrastructure: Folder
    templateServices: TemplateService[]
    className?: string
}

export const WalkMobilePreview = ({
    infrastructure,
    templateServices,
    className,
}: Props) => {
    const classes = useStyles()
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
    const appliedAutoSelect = useRef<Set<number>>(new Set())

    const chosenUnit = useMemo(() => firstSchedulerUnit(infrastructure), [
        infrastructure,
    ])

    const areas = chosenUnit?.areas ?? []

    useEffect(() => {
        if (areas.length === 0) {
            return
        }
        setSelectedKeys((current) => {
            const next = new Set(current)
            let changed = false
            templateServices.forEach((service) => {
                if (!service.auto_select_areas) {
                    if (appliedAutoSelect.current.has(service.id)) {
                        areas.forEach((area) => {
                            if (
                                next.delete(selectionKey(service.id, area.id))
                            ) {
                                changed = true
                            }
                        })
                        appliedAutoSelect.current.delete(service.id)
                    }
                    return
                }
                if (appliedAutoSelect.current.has(service.id)) {
                    return
                }
                areas.forEach((area) => {
                    const blocked =
                        isPaintCategory(service.service.service_category) &&
                        templateServices.some(
                            (other) =>
                                other.id !== service.id &&
                                isPaintCategory(
                                    other.service.service_category,
                                ) &&
                                next.has(selectionKey(other.id, area.id)),
                        )
                    if (blocked) {
                        return
                    }
                    const key = selectionKey(service.id, area.id)
                    if (!next.has(key)) {
                        next.add(key)
                        changed = true
                    }
                })
                appliedAutoSelect.current.add(service.id)
            })
            return changed ? next : current
        })
    }, [areas, templateServices])

    const areaIsBlocked = (
        service: TemplateService,
        areaId: number,
        keys: Set<string>,
    ) => {
        if (!isPaintCategory(service.service.service_category)) {
            return false
        }
        return templateServices.some(
            (other) =>
                other.id !== service.id &&
                isPaintCategory(other.service.service_category) &&
                keys.has(selectionKey(other.id, areaId)),
        )
    }

    const toggleArea = (service: TemplateService, areaId: number) => {
        const key = selectionKey(service.id, areaId)
        setSelectedKeys((current) => {
            const next = new Set(current)
            if (next.has(key)) {
                next.delete(key)
                return next
            }
            if (areaIsBlocked(service, areaId, current)) {
                return current
            }
            next.add(key)
            return next
        })
    }

    const selectAll = (service: TemplateService) => {
        setSelectedKeys((current) => {
            const next = new Set(current)
            areas.forEach((area) => {
                if (areaIsBlocked(service, area.id, current)) {
                    return
                }
                next.add(selectionKey(service.id, area.id))
            })
            return next
        })
    }

    const deselectAll = (service: TemplateService) => {
        setSelectedKeys((current) => {
            const next = new Set(current)
            areas.forEach((area) => {
                next.delete(selectionKey(service.id, area.id))
            })
            return next
        })
    }

    return (
        <div className={className}>
            <div className={classes.previewLabel}>Preview</div>
            <div className={classes.phone}>
                <div className={classes.header}>
                    {chosenUnit ? chosenUnit.name : 'No unit'}
                </div>
                <div className={classes.body}>
                    {chosenUnit == null ? (
                        <div className={classes.empty}>No unit to preview</div>
                    ) : templateServices.length === 0 ? (
                        <div className={classes.empty}>
                            No services selected for this project
                        </div>
                    ) : (
                        <>
                            {areas.length === 0 && (
                                <div className={classes.empty}>
                                    No areas in this unit
                                </div>
                            )}
                            {templateServices.map((service) => {
                                const icon = getServiceImageDetails(
                                    service.service.id,
                                    service.service.service_category,
                                ).icon
                                const selectable = areas.filter(
                                    (area) =>
                                        !areaIsBlocked(
                                            service,
                                            area.id,
                                            selectedKeys,
                                        ),
                                )
                                const selectedCount = areas.filter((area) =>
                                    selectedKeys.has(
                                        selectionKey(service.id, area.id),
                                    ),
                                ).length
                                const allSelected =
                                    (selectable.length > 0 &&
                                        selectable.every((area) =>
                                            selectedKeys.has(
                                                selectionKey(
                                                    service.id,
                                                    area.id,
                                                ),
                                            ),
                                        )) ||
                                    (selectable.length === 0 &&
                                        selectedCount > 0)
                                const showBulk =
                                    selectable.length > 0 || selectedCount > 0

                                return (
                                    <div
                                        key={service.id}
                                        className={classes.service}
                                    >
                                        <div className={classes.titleRow}>
                                            {icon != null && (
                                                <div
                                                    className={classes.iconOrb}
                                                >
                                                    <img
                                                        src={icon}
                                                        alt=""
                                                        className={classes.icon}
                                                    />
                                                </div>
                                            )}
                                            <div
                                                className={classes.serviceName}
                                            >
                                                {service.service.name}
                                            </div>
                                            {showBulk && (
                                                <button
                                                    type="button"
                                                    className={
                                                        allSelected
                                                            ? classes.bulkClear
                                                            : classes.bulk
                                                    }
                                                    onClick={() => {
                                                        if (allSelected) {
                                                            deselectAll(service)
                                                        } else {
                                                            selectAll(service)
                                                        }
                                                    }}
                                                >
                                                    {allSelected
                                                        ? 'Deselect all'
                                                        : 'Select all'}
                                                </button>
                                            )}
                                            <ChatBubbleOutlineIcon
                                                className={classes.messageIcon}
                                            />
                                        </div>
                                        <div className={classes.areaWrap}>
                                            {areas.map((area) => {
                                                const selected = selectedKeys.has(
                                                    selectionKey(
                                                        service.id,
                                                        area.id,
                                                    ),
                                                )
                                                const blocked = areaIsBlocked(
                                                    service,
                                                    area.id,
                                                    selectedKeys,
                                                )
                                                return (
                                                    <button
                                                        key={area.id}
                                                        type="button"
                                                        disabled={blocked}
                                                        className={
                                                            selected
                                                                ? classes.chipSelected
                                                                : blocked
                                                                ? classes.chipBlocked
                                                                : classes.chip
                                                        }
                                                        onClick={() =>
                                                            toggleArea(
                                                                service,
                                                                area.id,
                                                            )
                                                        }
                                                    >
                                                        {areaLabel(area)}
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    </div>
                                )
                            })}
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}

const useStyles = makeStyles((theme) => ({
    previewLabel: {
        flexShrink: 0,
        marginBottom: theme.spacing(1),
        fontWeight: 700,
        fontSize: 16,
        color: theme.palette.text.primary,
        textAlign: 'center',
    },
    phone: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        border: '10px solid #1c1c1e',
        borderRadius: 36,
        backgroundColor: '#fff',
        overflow: 'hidden',
        boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
    },
    header: {
        flexShrink: 0,
        padding: '16px 16px 12px',
        textAlign: 'center',
        fontWeight: 600,
        fontSize: 16,
        color: theme.palette.grey[900],
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
        backgroundColor: '#fff',
    },
    body: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        backgroundColor: '#fff',
    },
    empty: {
        color: theme.palette.grey[700],
        fontSize: 16,
        textAlign: 'center',
        padding: theme.spacing(2),
    },
    service: {
        borderBottom: `1px solid ${theme.palette.grey[400]}`,
        padding: theme.spacing(2, 1, 4),
    },
    titleRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: theme.spacing(1),
    },
    iconOrb: {
        border: `1px solid ${theme.palette.grey[400]}`,
        borderRadius: '50%',
        padding: theme.spacing(1),
        backgroundColor: theme.palette.grey[100],
        marginRight: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    icon: {
        height: 20,
        width: 20,
        objectFit: 'contain',
    },
    serviceName: {
        flex: 1,
        minWidth: 0,
        fontSize: 16,
        fontWeight: 500,
        color: theme.palette.grey[900],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    bulk: {
        marginLeft: theme.spacing(1),
        padding: '6px 10px',
        borderRadius: 14,
        border: 0,
        backgroundColor: EZ_TURN_GREEN,
        color: '#fff',
        fontSize: 12,
        fontWeight: 700,
        fontFamily: 'inherit',
        cursor: 'pointer',
        flexShrink: 0,
    },
    bulkClear: {
        marginLeft: theme.spacing(1),
        padding: '6px 10px',
        borderRadius: 14,
        border: `1px solid ${theme.palette.grey[400]}`,
        backgroundColor: theme.palette.grey[200],
        color: theme.palette.grey[800],
        fontSize: 12,
        fontWeight: 700,
        fontFamily: 'inherit',
        cursor: 'pointer',
        flexShrink: 0,
    },
    messageIcon: {
        marginLeft: theme.spacing(1),
        color: theme.palette.grey[800],
        fontSize: 24,
        flexShrink: 0,
    },
    areaWrap: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
    },
    chip: {
        width: 'calc((100% - 30px) / 3)',
        marginRight: 15,
        minHeight: 43,
        marginTop: 0,
        padding: '12px 4px',
        borderRadius: 5,
        border: '1px solid black',
        backgroundColor: 'transparent',
        color: 'black',
        fontSize: 15,
        fontFamily: 'inherit',
        lineHeight: '18px',
        textAlign: 'center',
        cursor: 'pointer',
        boxSizing: 'border-box',
        '&:nth-child(3n)': {
            marginRight: 0,
        },
        '&:nth-child(n + 4)': {
            marginTop: 15,
        },
    },
    chipSelected: {
        width: 'calc((100% - 30px) / 3)',
        marginRight: 15,
        minHeight: 43,
        marginTop: 0,
        padding: '12px 4px',
        borderRadius: 5,
        border: 0,
        backgroundColor: EZ_TURN_GREEN,
        color: '#fff',
        fontSize: 15,
        fontFamily: 'inherit',
        lineHeight: '18px',
        textAlign: 'center',
        cursor: 'pointer',
        boxSizing: 'border-box',
        '&:nth-child(3n)': {
            marginRight: 0,
        },
        '&:nth-child(n + 4)': {
            marginTop: 15,
        },
    },
    chipBlocked: {
        width: 'calc((100% - 30px) / 3)',
        marginRight: 15,
        minHeight: 43,
        marginTop: 0,
        padding: '12px 4px',
        borderRadius: 5,
        border: `1px solid ${theme.palette.grey[400]}`,
        backgroundColor: theme.palette.grey[200],
        color: 'black',
        fontSize: 15,
        fontFamily: 'inherit',
        lineHeight: '18px',
        textAlign: 'center',
        cursor: 'default',
        opacity: 0.55,
        boxSizing: 'border-box',
        '&:nth-child(3n)': {
            marginRight: 0,
        },
        '&:nth-child(n + 4)': {
            marginTop: 15,
        },
    },
}))
